import {
    Box,
    Typography,
    Drawer,
    IconButton,
    Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import API from "../api/api";
import GlobalSkeleton from "../components/GlobalSkeleton";
import { useState, useEffect, useCallback } from "react";
import LocationPage from "./LocationPage";

export default function AddressDrawer({ open, onClose, onSelect }) {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [, setSelectedId] = useState(null);

    const fetchAddresses = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;

        try {
            setLoading(true);
            const res = await API.get(`/user/${user.id}/addresses`);
            
            if (Array.isArray(res.data)) {
                setAddresses(res.data);
                const defaultAddr = res.data.find(a => a.is_default);
                setSelectedId(defaultAddr?.id || null);
            } else {
                setAddresses([]);
                setSelectedId(null);
            }
        } catch (err) {
            console.error("Error fetching addresses:", err);
            setAddresses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchAddresses();
    }, [open, fetchAddresses]);

    useEffect(() => {
        const handleUpdate = () => {
            fetchAddresses();
        };
        window.addEventListener("storage", handleUpdate);
        return () => window.removeEventListener("storage", handleUpdate);
    }, [fetchAddresses]);

    const handleSelect = async (addr) => {
        try {
            setSelectedId(addr.id);
            setAddresses(prev =>
                prev.map(a => ({
                    ...a,
                    is_default: a.id === addr.id
                }))
            );

            setTimeout(async () => {
                await API.put(`/user/address/${addr.id}/set-default`);
                onSelect(addr);
                localStorage.setItem("addressUpdated", Date.now());
                onClose();
            }, 350);

        } catch (err) {
            console.error("Error setting default address:", err);
        }
    };

    const sortedAddresses = [...addresses].sort((a, b) => {
        if (a.is_default) return -1;
        if (b.is_default) return 1;
        return 0;
    });

    return (
        <>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        borderRadius: "24px 24px 0 0",
                        overflow: "visible",
                        height: "75vh",
                        maxHeight: "75vh",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#FFFDF8"
                    },
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute", top: -60, left: "50%", transform: "translateX(-50%)",
                        bgcolor: "rgba(58, 44, 35, 0.7)", color: "#FFFFFF", zIndex: 1300,
                        border: "1px solid rgba(255,255,255,0.2)",
                        "&:hover": { bgcolor: "rgba(58, 44, 35, 0.9)" },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ p: 2, overflowY: "auto", height: "100%" }}>
                    <>
                        <Box sx={{ mb: 3, mt: 1, textAlign: "center" }}>
                            <Typography variant="h6" fontWeight="800" sx={{ color: "#3A2C23" }}>
                                Select Delivery Address
                            </Typography>

                            <Button
                                fullWidth
                                variant="contained"
                                disableElevation
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    onClose();
                                    setAddDrawerOpen(true);
                                }}
                                sx={{
                                    mt: 2.5, py: 1.5, borderRadius: '16px',
                                    bgcolor: '#F8C332', color: '#3A2C23', fontWeight: '800',
                                    textTransform: 'none', fontSize: '1.05rem',
                                    boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': { bgcolor: '#F5B71D', transform: 'translateY(-2px)' },
                                }}
                            >
                                Add New Address
                            </Button>
                        </Box>

                        {loading && (
                            <Box sx={{ mt: 2 }}>
                                <GlobalSkeleton type="default" />
                                <GlobalSkeleton type="default" />
                            </Box>
                        )}

                        {!loading && addresses.length === 0 && (
                            <Typography textAlign="center" sx={{ color: "#6E655E", fontWeight: 600, mt: 2 }}>
                                No saved addresses
                            </Typography>
                        )}

                        {!loading &&
    sortedAddresses.map((addr) => (
        <Box
            key={addr.id}
            sx={{
                p: 2.5, mb: 2, borderRadius: '20px',
                bgcolor: addr.is_default ? '#eefcd9' : '#FFFFFF',
                border: addr.is_default ? "2px solid #6D4C41" : "none", // Removed unselected flat border
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', // 3D Shadow applied to all
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                    transform: addr.is_default ? "scale(1.02) translateY(-3px)" : "translateY(-3px)", // Preserved selected scale + lift
                    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)', // Enhanced hover shadow
                    borderColor: addr.is_default ? "#6A9E3A" : "transparent"
                },
                "&:active": { transform: "translateY(1px)" },
                transform: addr.is_default ? "scale(1.02)" : "scale(1)",
            }}
            onClick={() => handleSelect(addr)}
        >
                                    <Typography fontWeight="800" sx={{ mb: 0.5, color: addr.is_default ? "#6A9E3A" : "#3A2C23", textTransform: 'uppercase' }}>
                                        {addr.address_type || "ADDRESS"}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: addr.is_default ? "#4d7827" : "#6E655E",
                                            fontWeight: 500, lineHeight: 1.6, wordBreak: "break-word"
                                        }}
                                    >
                                        {[addr.flat, addr.building, addr.landmark].filter(Boolean).join(", ")}
                                    </Typography>
                                </Box>
                            ))}
                    </>
                </Box>
            </Drawer>

            <Drawer
                anchor="bottom"
                open={addDrawerOpen}
                onClose={() => setAddDrawerOpen(false)}
                disableEnforceFocus 
                disableAutoFocus   
                disableRestoreFocus 
                PaperProps={{
                    sx: {
                        borderRadius: "0", 
                        bgcolor: "#FFFDF8",
                        height: "100dvh",   // 👈 FIX: dynamic viewport height prevents browser UI overlap
                        maxHeight: "100dvh",
                        overflow: "hidden" 
                    }
                }}
            >
                {/* 👈 FIX: Removed the <Box sx={{ p: 2 }}> padding wrapper so map is edge-to-edge */}
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <LocationPage
                        isDrawer
                        onCancel={() => setAddDrawerOpen(false)}
                        onSuccess={(newAddr) => {
                            setAddDrawerOpen(false);
                            fetchAddresses();    
                            onSelect(newAddr);    
                            localStorage.setItem("addressUpdated", Date.now());
                        }}
                    />
                </Box>
            </Drawer>
        </>
    );
}